import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  Renderer2,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { IConnectContact } from '@epicuro-next/data/connect';
import {
  LocalAudioTrack,
  LocalAudioTrackPublication,
  LocalVideoTrack,
  LocalVideoTrackPublication,
  Participant,
  TrackPublication,
} from 'twilio-video';

import { ParticipantMediaStatus } from '../../services/connect-video.store';

@Component({
  selector: 'epc-participant-camera',
  templateUrl: './participant.component.html',
  styleUrls: ['./participant.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ParticipantComponent implements OnChanges, OnDestroy {
  @Input()
  public participant: Participant;

  @Input()
  public isLocal = false;

  @Input()
  public mediaStatus: ParticipantMediaStatus;

  @Input()
  public contact: IConnectContact;

  @ViewChild('videoRef', { static: true })
  public videoRef: ElementRef<HTMLVideoElement>;

  @ViewChild('videoContainerRef', { static: true })
  public videoContainerRef: ElementRef<HTMLVideoElement>;

  @ViewChild('audioRef', { static: true })
  public audioRef: ElementRef<HTMLAudioElement>;

  public isScreenSharing = false;

  constructor(private renderer: Renderer2, private cdr: ChangeDetectorRef) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.participant?.currentValue) {
      this.handleTrackPublications();
    }
  }

  public ngOnDestroy() {
    if (this.participant) {
      this.participant.removeAllListeners();
      this.participant.tracks.forEach((publication) => {
        publication.removeAllListeners();
      });
    }
  }

  private handleTrackPublications() {
    this.participant.tracks.forEach((publication) => {
      this.trackPublished(publication);
    });

    this.participant.on('trackPublished', (publication: TrackPublication) => {
      if (!this.isLocal) {
        this.isScreenSharing = publication.trackName.includes('ScreenShare');
      }
      this.trackPublished(publication);
    });
  }

  private trackPublished(publication: TrackPublication) {
    if (this.isParticipantPublication(publication) && publication.track) {
      this.attachTrack(publication.track);
    }

    publication.on('subscribed', (track) => {
      this.attachTrack(track);
    });

    publication.on('unsubscribed', (track) => {
      this.detachTrack(track);
    });
  }

  private attachTrack(track: LocalVideoTrack | LocalAudioTrack) {
    if (track.kind === 'audio') {
      track.attach(this.audioRef.nativeElement);
    }
    if (track.kind === 'video') {
      this.renderer.appendChild(
        this.videoContainerRef.nativeElement,
        track.attach(),
      );
      this.cdr.markForCheck();
    }
  }

  private detachTrack(track: LocalVideoTrack | LocalAudioTrack) {
    if (track.kind === 'audio') {
      track.detach(this.audioRef.nativeElement);
    }
    if (track.kind === 'video') {
      if (!this.isLocal && track.name.includes('ScreenShare')) {
        this.isScreenSharing = false;
        this.cdr.markForCheck();
      }
      track.detach().forEach((element) => element.remove());
    }
  }

  private isParticipantPublication(
    participant: TrackPublication,
  ): participant is LocalVideoTrackPublication | LocalAudioTrackPublication {
    return (
      !!participant &&
      ((participant as LocalVideoTrackPublication).kind !== undefined ||
        (participant as LocalAudioTrackPublication).kind !== undefined)
    );
  }
}
